import { apply, nodeMatrix } from '@repo/design-renderer';
import type { FlatView } from './flat-mode-mapping';
import { type BoxNode, handlePoints, isRightAngle, ROTATE_HANDLE_RADIUS_PX } from './handles';

interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

/** 상자 바깥으로 띄우는 화면 간격(px). */
const HINT_GAP_PX = 12;
/** 각도 표시와 회전 손잡이 사이에 남기는 빈틈(px). */
const BADGE_GAP_PX = 6;
/** 각도 표시 상자의 반 크기(px) — 표시는 이 크기(40×20)로 고정해 그린다. */
export const ROTATION_BADGE_HALF_PX = { x: 20, y: 10 } as const;

const toScreen = (view: FlatView, xMm: number, yMm: number): ScreenPoint => ({
  x: view.offsetX + xMm * view.pxPerMmX,
  y: view.offsetY + yMm * view.pxPerMmY,
});

/** 노드 상자 네 모서리(회전 반영)의 화면 외접 사각형과 가운데. 핸들 판정처럼 플립은 무시한다. */
const screenBoundsOf = (node: BoxNode, view: FlatView) => {
  const m = nodeMatrix(node.type === 'image' ? { ...node, flipX: false, flipY: false } : node);
  const corners = [
    { x: 0, y: 0 },
    { x: node.width, y: 0 },
    { x: node.width, y: node.height },
    { x: 0, y: node.height },
  ].map((corner) => {
    const p = apply(m, corner);
    return toScreen(view, p.x, p.y);
  });
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  const left = Math.min(...xs);
  const right = Math.max(...xs);
  const top = Math.min(...ys);
  const bottom = Math.max(...ys);
  return { left, right, top, bottom, center: { x: (left + right) / 2, y: (top + bottom) / 2 } };
};

const rotateHandleOnScreen = (node: BoxNode, view: FlatView): ScreenPoint | null => {
  const handle = handlePoints(node, { x: view.pxPerMmX, y: view.pxPerMmY }).find(
    (point) => point.kind === 'rotate',
  );
  return handle === undefined ? null : toScreen(view, handle.xMm, handle.yMm);
};

export type HintSide = 'below' | 'above' | 'left' | 'right';

export interface HintPlacement {
  /** 안내의 기준점(화면 px). side 쪽으로 상자에서 HINT_GAP_PX 떨어진 변의 가운데다. */
  readonly left: number;
  readonly top: number;
  readonly side: HintSide;
}

/**
 * 글자 고치기 안내 자리 — 회전 손잡이가 있는 쪽의 반대편(화면 외접 사각형 바깥)에 둔다.
 * 손잡이가 가운데에서 위·아래로 더 멀면 아래·위, 좌우로 더 멀면 오른쪽·왼쪽의 반대다.
 */
export const textHintPlacement = (node: BoxNode, view: FlatView): HintPlacement => {
  const bounds = screenBoundsOf(node, view);
  const handle = rotateHandleOnScreen(node, view) ?? { x: bounds.center.x, y: bounds.top };
  const dx = handle.x - bounds.center.x;
  const dy = handle.y - bounds.center.y;
  if (Math.abs(dy) >= Math.abs(dx))
    return dy <= 0
      ? { left: bounds.center.x, top: bounds.bottom + HINT_GAP_PX, side: 'below' }
      : { left: bounds.center.x, top: bounds.top - HINT_GAP_PX, side: 'above' };
  return dx >= 0
    ? { left: bounds.left - HINT_GAP_PX, top: bounds.center.y, side: 'left' }
    : { left: bounds.right + HINT_GAP_PX, top: bounds.center.y, side: 'right' };
};

export interface RotationFeedback {
  /** 노드 가운데(화면 px) — 직각 안내선이 지나는 점. */
  readonly center: ScreenPoint;
  /** 각도 표시 자리(화면 px, 상자 가운데). */
  readonly badge: ScreenPoint;
  /** 회전 손잡이 가운데(화면 px). */
  readonly handle: ScreenPoint;
  /** 0~359° 로 반올림한 각도 문구. */
  readonly label: string;
  /** 직각에 붙었으면 안내선 방향(0·180° 수평, 90·270° 수직), 아니면 null. */
  readonly guide: 'horizontal' | 'vertical' | null;
}

/** 각도 표시 상자와 손잡이 동그라미 사이의 빈틈(px) — 음수면 겹친다. */
const clearanceOf = (badge: ScreenPoint, handle: ScreenPoint): number => {
  const nearestX = clampTo(
    handle.x,
    badge.x - ROTATION_BADGE_HALF_PX.x,
    badge.x + ROTATION_BADGE_HALF_PX.x,
  );
  const nearestY = clampTo(
    handle.y,
    badge.y - ROTATION_BADGE_HALF_PX.y,
    badge.y + ROTATION_BADGE_HALF_PX.y,
  );
  return Math.hypot(handle.x - nearestX, handle.y - nearestY) - ROTATE_HANDLE_RADIUS_PX;
};

const labelOf = (node: BoxNode): string => {
  const deg = Math.round(node.rotationDeg);
  return `${((deg % 360) + 360) % 360}°`;
};

const guideOf = (node: BoxNode): RotationFeedback['guide'] =>
  isRightAngle(node.rotationDeg)
    ? Math.abs(Math.round(node.rotationDeg / 90)) % 2 === 0
      ? 'horizontal'
      : 'vertical'
    : null;

const insideViewport = (point: ScreenPoint, viewport: ViewportSize): boolean =>
  point.x - ROTATION_BADGE_HALF_PX.x >= 0 &&
  point.x + ROTATION_BADGE_HALF_PX.x <= viewport.width &&
  point.y - ROTATION_BADGE_HALF_PX.y >= 0 &&
  point.y + ROTATION_BADGE_HALF_PX.y <= viewport.height;

const clampTo = (value: number, min: number, max: number): number =>
  max < min ? (min + max) / 2 : Math.min(max, Math.max(min, value));

/** 캔버스 화면 크기(px). */
export interface ViewportSize {
  readonly width: number;
  readonly height: number;
}

/**
 * 회전 핸들을 끄는 동안 보일 각도 표시·직각 안내선. 각도 표시는 노드 가운데→손잡이 방향으로 손잡이 너머에 둔다 —
 * 그 방향으로 표시 상자가 뻗는 길이(반 크기의 투영) + 손잡이 반지름 + 빈틈만큼 떨어뜨려 어느 각도에서도 손잡이와
 * 겹치지 않는다. 캔버스(viewport) 밖으로 나가면 가운데 반대편으로 뒤집고, 그래도 넘치면 안으로 당긴다.
 */
export const rotationFeedback = (
  node: BoxNode,
  view: FlatView,
  viewport: ViewportSize | null = null,
): RotationFeedback => {
  const center = toScreen(view, node.x + node.width / 2, node.y + node.height / 2);
  const handle = rotateHandleOnScreen(node, view) ?? center;
  const dx = handle.x - center.x;
  const dy = handle.y - center.y;
  const length = Math.hypot(dx, dy);
  const ux = length === 0 ? 0 : dx / length;
  const uy = length === 0 ? -1 : dy / length;
  const reach =
    ROTATION_BADGE_HALF_PX.x * Math.abs(ux) +
    ROTATION_BADGE_HALF_PX.y * Math.abs(uy) +
    ROTATE_HANDLE_RADIUS_PX +
    BADGE_GAP_PX;
  const outward = { x: handle.x + ux * reach, y: handle.y + uy * reach };
  const flipped = { x: center.x - ux * (length + reach), y: center.y - uy * (length + reach) };
  const chosen =
    viewport === null || insideViewport(outward, viewport) || !insideViewport(flipped, viewport)
      ? outward
      : flipped;
  if (viewport === null)
    return { center, badge: chosen, handle, label: labelOf(node), guide: guideOf(node) };
  const clamp = (point: ScreenPoint): ScreenPoint => ({
    x: clampTo(point.x, ROTATION_BADGE_HALF_PX.x, viewport.width - ROTATION_BADGE_HALF_PX.x),
    y: clampTo(point.y, ROTATION_BADGE_HALF_PX.y, viewport.height - ROTATION_BADGE_HALF_PX.y),
  });
  // 바깥·반대편 둘 다 캔버스를 넘어 안으로 당겼더니 손잡이와 겹치면 — 손잡이 옆(±90°)으로 비켜 둔다.
  const sideways = (sign: 1 | -1): ScreenPoint => {
    const px = -uy * sign;
    const py = ux * sign;
    const side =
      ROTATION_BADGE_HALF_PX.x * Math.abs(px) +
      ROTATION_BADGE_HALF_PX.y * Math.abs(py) +
      ROTATE_HANDLE_RADIUS_PX +
      BADGE_GAP_PX;
    return clamp({ x: handle.x + px * side, y: handle.y + py * side });
  };
  const candidates = [clamp(chosen), sideways(1), sideways(-1)];
  const badge =
    candidates.find((point) => clearanceOf(point, handle) >= BADGE_GAP_PX - 1e-6) ??
    candidates.reduce((best, point) =>
      clearanceOf(point, handle) > clearanceOf(best, handle) ? point : best,
    );
  return { center, badge, handle, label: labelOf(node), guide: guideOf(node) };
};
